import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState, IconButton, Pagination, SearchInput, Segmented, TableSkeleton } from "../../../components/ui";
import { getLogFile, getLogs } from "../../../services/monitoring";
import { ContextList, EVENT_LABELS, formatDate, LevelBadge } from "./shared";

const LEVEL_FILTERS = [
  { value: "", label: "All" },
  { value: "received", label: "Payments received" },
  { value: "problems", label: "Problems" },
  { value: "error", label: "Errors" },
];

const CATEGORIES = [
  { value: "", label: "All" },
  { value: "payment", label: "Payments" },
  { value: "cinetpay", label: "CinetPay" },
  { value: "pawapay", label: "pawaPay" },
  { value: "paystack", label: "Paystack" },
  { value: "stripe", label: "Stripe" },
  { value: "security", label: "Security" },
  { value: "system", label: "Server" },
  { value: "admin", label: "Administrators", adminActivity: true },
];

/**
 * Structured log of the application (everything: payments received, problems,
 * admin actions when allowed), cleaned of sensitive data.
 */
const Events = ({ initialLevel = "", showAdminActivity = false }) => {
  const { t } = useTranslation();
  const [level, setLevel] = useState(initialLevel);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => getLogs({ level, category, q: search, page }).then(setData), [level, category, search, page]);
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const items = data?.items || [];
  return (
    <section className="ui-card">
      <div className="ui-toolbar logs-toolbar">
        <div className="ui-chips">
          {LEVEL_FILTERS.map((f) => (
            <button key={f.value} type="button" className="ui-chip" aria-pressed={level === f.value} onClick={() => { setLevel(f.value); setPage(1); }}>
              {t(f.label)}
            </button>
          ))}
          <select className="ui-input logs-select" value={category} aria-label={t("Category")} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            {CATEGORIES.filter((c) => !c.adminActivity || showAdminActivity).map((c) => (
              <option key={c.value} value={c.value}>{t(c.label)}</option>
            ))}
          </select>
        </div>
        <div className="logs-toolbar-end">
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t("Message, event or transaction")} />
          <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />
        </div>
      </div>
      {!data ? (
        <TableSkeleton />
      ) : !items.length ? (
        <EmptyState icon="ri-checkbox-circle-line">{level === "problems" ? t("No problem recorded") : t("Nothing here")}</EmptyState>
      ) : (
        <ul className="logs-events">
          {items.map((log) => (
            <li key={log.id} data-open={open === log.id || undefined}>
              <button type="button" className="logs-event-row" onClick={() => setOpen(open === log.id ? null : log.id)} aria-expanded={open === log.id}>
                <span className="logs-event-date">{formatDate(log.at)}</span>
                <LevelBadge level={log.level} />
                <span className="logs-event-category">{log.category}</span>
                <span className="logs-event-text">
                  <strong>{t(EVENT_LABELS[log.event] || log.event || "")}</strong>
                  <span>{log.message}</span>
                </span>
                {log.count > 1 && <span className="logs-count">×{log.count}</span>}
                <i className="ri-arrow-down-s-line logs-chevron" aria-hidden="true"></i>
              </button>
              {open === log.id && (
                <div className="logs-event-details">
                  {log.refs?.transactionId && <p><span className="ui-label">{t("Transaction")}</span> <code>{log.refs.transactionId}</code></p>}
                  {log.refs?.stripeId && <p><span className="ui-label">Stripe</span> <code>{log.refs.stripeId}</code></p>}
                  {log.refs?.user && <p><span className="ui-label">{t("User")}</span> <code>{log.refs.user}</code></p>}
                  <ContextList context={log.context} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {data && <Pagination page={page} pageCount={Math.max(1, Math.ceil(data.total / data.pageSize))} total={data.total} pageSize={data.pageSize} onChange={setPage} />}
    </section>
  );
};

/** End of the server log files (cleaned). */
const ServerFile = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("app");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    setData(null);
    getLogFile(name, 500).then(setData);
  }, [name]);
  useEffect(() => {
    load();
  }, [load]);
  const lines = (data?.lines || []).filter((line) => !filter || line.toLowerCase().includes(filter.toLowerCase()));
  return (
    <section className="ui-card">
      <div className="ui-toolbar">
        <Segmented
          label={t("File")}
          value={name}
          onChange={setName}
          options={[
            { value: "app", label: t("Server (app.log)") },
            { value: "jw", label: t("JW views sync") },
          ]}
        />
        <div className="logs-toolbar-end">
          <SearchInput value={filter} onChange={setFilter} placeholder={t("Filter lines")} />
          <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />
        </div>
      </div>
      {!data ? (
        <TableSkeleton />
      ) : (
        <pre className="logs-file">{lines.length ? lines.slice().reverse().join("\n") : t("Empty file")}</pre>
      )}
      <p className="ui-hint logs-note">{t("Last 500 lines, newest first. Passwords, tokens, keys and database addresses are masked.")}</p>
    </section>
  );
};

const Journal = ({ initialLevel, showAdminActivity }) => {
  const { t } = useTranslation();
  const [view, setView] = useState("events");
  return (
    <div className="logs-section">
      <div className="ui-chips logs-subnav">
        <button type="button" className="ui-chip" aria-pressed={view === "events"} onClick={() => setView("events")}>
          <i className="ri-file-list-2-line" aria-hidden="true"></i> {t("Events")}
        </button>
        <button type="button" className="ui-chip" aria-pressed={view === "file"} onClick={() => setView("file")}>
          <i className="ri-terminal-box-line" aria-hidden="true"></i> {t("Server files")}
        </button>
      </div>
      {view === "events" ? (
        <Events initialLevel={initialLevel} showAdminActivity={showAdminActivity} />
      ) : (
        <ServerFile />
      )}
    </div>
  );
};

export default Journal;
